/**
 * Demo launcher — single-button scenario trigger for live demos.
 *
 * ADMIN-only. Fires preset scenarios against the simulator via the
 * /api/v1/demo/scenario proxy. After a click, the operator dashboard
 * shows the new DRAFT incident within ~7 seconds (classifier + Claude).
 *
 * Do NOT fumble with curl during a demo — click here instead.
 */

import { useState } from "react";
import { Zap, PlayCircle, Activity, Truck, Hammer, Train, CheckCircle, AlertTriangle } from "lucide-react";
import { AdminNav } from "@/components/AdminNav";
import { api, ApiError } from "@/lib/api";

interface DemoResponse {
  scenario: string;
  fired_event_ids: string[];
}

interface DemoButton {
  id: string;
  label: string;
  description: string;
  expected: string;
  icon: typeof Zap;
  accent: "rose" | "amber" | "teal" | "sky" | "violet";
}

const SCENARIOS: DemoButton[] = [
  {
    id: "basic_intrusion",
    label: "Third-party intrusion",
    description: "High-energy excavation near KP-142 with no work permit",
    expected: "Expected: INTRUSION_THIRD_PARTY, ~70% confidence, TELEGRAM + EMAIL dispatch",
    icon: AlertTriangle,
    accent: "rose",
  },
  {
    id: "pipeline_leak",
    label: "Pipeline leak signature",
    description: "Low-frequency persistent hiss consistent with gas escape",
    expected: "Expected: LEAK_SIGNAL, HIGH escalation_required, supervisor alerted",
    icon: Activity,
    accent: "rose",
  },
  {
    id: "vehicle_false_alarm",
    label: "Vehicle false alarm",
    description: "Truck driving along service road — false positive candidate",
    expected: "Expected: VEHICLE_VIBRATION, operator should DISMISS",
    icon: Truck,
    accent: "amber",
  },
  {
    id: "maintenance_shift",
    label: "Scheduled maintenance",
    description: "Contractor work within a permitted window — context suppresses alarm",
    expected: "Expected: MAINTENANCE_NOISE, low confidence, likely dismiss",
    icon: Hammer,
    accent: "teal",
  },
  {
    id: "train_in_schedule",
    label: "Scheduled train pass",
    description: "Rail movement matching train_schedules window",
    expected: "Expected: TRAIN_MOVEMENT, auto-context dampens confidence",
    icon: Train,
    accent: "sky",
  },
  {
    id: "intrusion_with_permit",
    label: "Intrusion (with active permit)",
    description: "Same acoustic signature as basic_intrusion but overlapping work permit",
    expected: "Expected: lower confidence — context override in action",
    icon: CheckCircle,
    accent: "violet",
  },
];

const ACCENTS: Record<DemoButton["accent"], string> = {
  rose: "border-rose-700/70 hover:border-rose-500 bg-rose-500/5 hover:bg-rose-500/10 text-rose-200",
  amber:
    "border-amber-700/70 hover:border-amber-500 bg-amber-500/5 hover:bg-amber-500/10 text-amber-200",
  teal: "border-teal-700/70 hover:border-teal-500 bg-teal-500/5 hover:bg-teal-500/10 text-teal-200",
  sky: "border-sky-700/70 hover:border-sky-500 bg-sky-500/5 hover:bg-sky-500/10 text-sky-200",
  violet:
    "border-violet-700/70 hover:border-violet-500 bg-violet-500/5 hover:bg-violet-500/10 text-violet-200",
};

export function DemoLauncherPage() {
  const [firing, setFiring] = useState<string | null>(null);
  const [lastFired, setLastFired] = useState<{ scenario: string; ids: string[]; at: Date } | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);

  async function fire(scenarioId: string) {
    setFiring(scenarioId);
    setError(null);
    try {
      const result = await api<DemoResponse>("/demo/scenario", {
        method: "POST",
        body: JSON.stringify({ scenario: scenarioId }),
      });
      setLastFired({
        scenario: result.scenario,
        ids: result.fired_event_ids,
        at: new Date(),
      });
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : err instanceof Error ? err.message : String(err),
      );
    } finally {
      setFiring(null);
    }
  }

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 p-6">
      <div className="max-w-5xl mx-auto space-y-6">
        <div className="flex items-start justify-between gap-6 flex-wrap">
          <div>
            <h1 className="text-xl font-semibold text-slate-100 flex items-center gap-2">
              <Zap className="text-teal-400 size-5" /> Demo launcher
            </h1>
            <p className="text-sm text-slate-400 mt-1 max-w-2xl">
              Single-click scenario triggers. Each button fires one event through the full pipeline
              (ingest → classifier → Claude reasoning → DRAFT incident). Open the dashboard in a
              second tab to watch the incident land in real time.
            </p>
          </div>
          <AdminNav />
        </div>

        {error && (
          <div
            className="border border-rose-800 bg-rose-950/50 rounded-lg px-4 py-3 text-sm text-rose-200"
            data-testid="demo-error"
            role="alert"
          >
            <strong>Simulator rejected:</strong> {error}
          </div>
        )}

        {lastFired && (
          <div
            className="border border-emerald-800 bg-emerald-950/40 rounded-lg px-4 py-3 text-sm text-emerald-200"
            data-testid="demo-last-fired"
          >
            <div className="flex items-center gap-2">
              <PlayCircle className="size-4" />
              <strong>Fired {lastFired.scenario}</strong>
              <span className="text-emerald-400/80">
                ({lastFired.ids.length} event{lastFired.ids.length === 1 ? "" : "s"} · at{" "}
                {lastFired.at.toLocaleTimeString()})
              </span>
            </div>
            <div className="mt-1 text-xs text-emerald-300/80 font-mono break-all">
              {lastFired.ids.join(", ")}
            </div>
            <div className="mt-2 text-xs text-emerald-300/80">
              Expect a DRAFT incident on the{" "}
              <a href="/app" className="underline">
                dashboard
              </a>{" "}
              within ~7 seconds.
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {SCENARIOS.map((s) => {
            const Icon = s.icon;
            const isFiring = firing === s.id;
            return (
              <button
                key={s.id}
                onClick={() => fire(s.id)}
                disabled={firing !== null}
                data-testid={`demo-scenario-${s.id}`}
                className={`rounded-xl border p-4 text-left transition disabled:opacity-40 disabled:cursor-wait ${ACCENTS[s.accent]}`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <Icon className="size-5" />
                  <div className="font-semibold text-sm">{s.label}</div>
                </div>
                <div className="text-xs text-slate-300/80 mb-2">{s.description}</div>
                <div className="text-[11px] text-slate-400/70 italic">{s.expected}</div>
                <div className="mt-3 text-xs font-mono">
                  {isFiring ? "firing…" : `> fire ${s.id}`}
                </div>
              </button>
            );
          })}
        </div>

        <div className="text-xs text-slate-500 border-t border-slate-800 pt-4">
          <strong>Tip for demos:</strong> open the dashboard in one tab, this page in another. Fire
          a scenario here, switch to the dashboard, and the new DRAFT incident lands within ~7s.
          Click the incident, review Claude's reasoning, then confirm/dismiss to trigger the
          Telegram + Email dispatch.
        </div>
      </div>
    </div>
  );
}
